import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import AcceptInvitePage from './components/auth/AcceptInvitePage';
import Dashboard from './components/Dashboard';
import XeroCallback from './components/XeroCallback';
import SuperAdminDashboard from './components/superadmin/SuperAdminDashboard';
import ContractorLogin from './components/contractor/ContractorLogin';
import ContractorInvite from './components/contractor/ContractorInvite';
import ContractorDashboard from './components/contractor/ContractorDashboard';
import ContractorPreviewPage from './components/contractor/ContractorPreviewPage';
import AuthService from './services/AuthService';
import { XeroConnectionProvider } from './contexts/XeroConnectionContext';

function App() {
  const [user, setUser] = useState(null);
  const [contractor, setContractor] = useState(null);

  // Debug contractor state changes
  useEffect(() => {
    console.log('🔍 Contractor state changed:', contractor);
  }, [contractor]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check regular user authentication
      const savedUser = AuthService.getUser();
      const token = AuthService.getToken();

      if (savedUser && token) {
        const isValid = await AuthService.validateToken();
        if (isValid) {
          setUser(savedUser);
        } else {
          AuthService.clearToken();
        }
      }

      // Check contractor authentication
      const savedContractor = localStorage.getItem('contractorUser');
      const contractorToken = localStorage.getItem('contractorToken');

      if (savedContractor && contractorToken) {
        try {
          const contractorUser = JSON.parse(savedContractor);
          setContractor(contractorUser);
        } catch (error) {
          console.error('Error parsing contractor user:', error);
          localStorage.removeItem('contractorUser');
          localStorage.removeItem('contractorToken');
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      AuthService.clearToken();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleRegister = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
    AuthService.clearToken();
  };

  const handleContractorLogout = () => {
    setContractor(null);
    localStorage.removeItem('contractorUser');
    localStorage.removeItem('contractorToken');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <XeroConnectionProvider>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />
              } 
            />
            <Route 
              path="/register" 
              element={
                user ? <Navigate to="/dashboard" replace /> : <RegisterPage onRegister={handleRegister} />
              } 
            />
            <Route 
              path="/invite/:token" 
              element={<AcceptInvitePage onLogin={handleLogin} />} 
            />
            <Route 
              path="/accept-invite" 
              element={<AcceptInvitePage onLogin={handleLogin} />} 
            />
            <Route path="/callback" element={<XeroCallback />} />
            
            {/* Contractor routes */}
            <Route 
              path="/contractor/login" 
              element={<ContractorLogin onLogin={setContractor} />} 
            />
            <Route 
              path="/contractor/invite/:token" 
              element={<ContractorInvite onRegister={setContractor} />} 
            />
            <Route
              path="/contractor/dashboard"
              element={
                contractor ? <ContractorDashboard contractor={contractor} onLogout={handleContractorLogout} /> : <Navigate to="/contractor/login" replace />
              }
            />
            {/* Admin preview — token is self-contained, no auth guard needed */}
            <Route path="/contractor/preview" element={<ContractorPreviewPage />} />
            
            {/* Super Admin route */}
            <Route path="/superadmin" element={<SuperAdminDashboard />} />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />
              } 
            />
            
            {/* Default redirect */}
            <Route 
              path="/" 
              element={
                user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              } 
            />
            
            {/* Catch all - redirect to login or dashboard */}
            <Route 
              path="*" 
              element={
                user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              } 
            />
          </Routes>
        </div>
      </XeroConnectionProvider>
    </Router>
  );
}

export default App;

